import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ManhwaCard from '@/components/feature/ManhwaCard';
import JsonLD from '@/components/feature/JsonLD';
import { manhwaList } from '@/mocks/manhwa';
import { categories } from '@/mocks/categories';

const ITEMS_PER_PAGE = 12;

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState<'latest' | 'rating' | 'views'>('latest');

  const activeCategory = categories.find(c => c.slug === slug) || categories[0];

  const filteredList = useMemo(() => {
    let list = slug === 'all' ? [...manhwaList] : manhwaList.filter(m => m.category === slug);

    switch (sortBy) {
      case 'rating':
        list.sort((a, b) => b.rating - a.rating);
        break;
      case 'views':
        list.sort((a, b) => b.views - a.views);
        break;
      case 'latest':
      default:
        list.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
        break;
    }

    return list;
  }, [slug, sortBy]);

  const totalPages = Math.ceil(filteredList.length / ITEMS_PER_PAGE);
  const currentItems = filteredList.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const pageJsonLD = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${activeCategory.name} - 韩漫之家`,
    description: `韩漫之家${activeCategory.name}分类，免费韩漫大全在线阅读。`,
    url: `http://topshine.net.cn/category/${slug}`,
    isPartOf: { '@type': 'WebSite', name: '韩漫之家', url: 'http://topshine.net.cn/' },
  };

  return (
    <>
      <JsonLD data={pageJsonLD} />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb items={[
              { label: '首页', href: '/' },
              { label: '分类浏览', href: '/category/all' },
              { label: activeCategory.name },
            ]} />

            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mt-2 mb-6">
              {activeCategory.name}漫画
            </h1>

            {/* Category Tabs */}
            <nav className="flex items-center gap-1 overflow-x-auto pb-3 mb-6 border-b border-background-200/70 scrollbar-hide" aria-label="分类导航">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                    slug === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'text-foreground-600 hover:bg-background-100 hover:text-foreground-900'
                  }`}
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className={`${cat.icon} text-xs`}></i>
                  </span>
                  {cat.name}
                </Link>
              ))}
            </nav>

            {/* Sort & Count */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
              <p className="text-sm text-foreground-500">
                共 <strong className="text-foreground-900">{filteredList.length}</strong> 部漫画
              </p>
              <div className="flex items-center gap-2">
                <span className="text-sm text-foreground-500">排序：</span>
                <div className="flex bg-background-100 rounded-full p-1">
                  {[
                    { key: 'latest' as const, label: '最新' },
                    { key: 'rating' as const, label: '评分' },
                    { key: 'views' as const, label: '人气' },
                  ].map((opt) => (
                    <button
                      key={opt.key}
                      onClick={() => { setSortBy(opt.key); setCurrentPage(1); }}
                      className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                        sortBy === opt.key
                          ? 'bg-background-50 text-foreground-900'
                          : 'text-foreground-500 hover:text-foreground-700'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5 mb-10">
              {currentItems.map((m) => (
                <ManhwaCard
                  key={m.id}
                  id={m.id}
                  title={m.title}
                  author={m.author}
                  cover={m.cover}
                  rating={m.rating}
                  views={m.views}
                  status={m.status}
                  category={m.category}
                  tags={m.tags}
                  chapters={m.chapters}
                />
              ))}
            </div>

            {/* Empty State */}
            {currentItems.length === 0 && (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                  <i className="ri-emotion-sad-line text-4xl text-foreground-300"></i>
                </div>
                <p className="text-foreground-500">该分类暂无漫画，请浏览其他分类</p>
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <nav className="flex items-center justify-center gap-2 pb-12" aria-label="分页导航">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="w-9 h-9 flex items-center justify-center rounded-lg text-sm font-medium text-foreground-600 hover:bg-background-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  aria-label="上一页"
                >
                  <i className="ri-arrow-left-s-line"></i>
                </button>

                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`w-9 h-9 flex items-center justify-center rounded-lg text-sm font-medium transition-colors ${
                      page === currentPage
                        ? 'bg-primary-500 text-background-50'
                        : 'text-foreground-600 hover:bg-background-100'
                    }`}
                  >
                    {page}
                  </button>
                ))}

                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="w-9 h-9 flex items-center justify-center rounded-lg text-sm font-medium text-foreground-600 hover:bg-background-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  aria-label="下一页"
                >
                  <i className="ri-arrow-right-s-line"></i>
                </button>
              </nav>
            )}
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}